import React, { useState, useEffect } from "react";
import {
  X,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  IndianRupee,
  TrendingUp,
  CalendarCheck,
  Plane,
  MessageCircle,
  ChevronUp,
  ChevronDown,
  Activity,
  Building2,
  Layers,
  Map,
  GraduationCap,
  User,
  FileText,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { handleOutsideClick } from "../../../../Helpers/HandleOutsideClick";

// ADD THESE NEW PROPS
const CandidateDetailsModal = ({
  show,
  onClose,
  candidateDetails,
  selectedCandidate,
  showSystemRemarkButton = false,
  showResumeButton = false,
  showInterviewSection = false,

  /* NEW */
  showInterviewDetailsSection = false,
  interviewDetails = [],
  onScheduleClick,

  onViewResume,
  onViewSystemRemark,
  systemRemarkData,
  showSystemRemark,
  modalActionType,
  remark,
  setRemark,
  onActionSubmit,
  actionLoading,
  interviewDateTime,
  setInterviewDateTime,
  interviewerList,
  setInterviewerList,
  onOpenAddInterviewer,
  handleDragEnd,
  onViewFeedback,
  systemRemarkLoading,
}) => {
  const [isJobOpen, setIsJobOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  useEffect(() => {
    if (show) {
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      setShouldRender(true);

      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);

      setTimeout(() => {
        setShouldRender(false);
      }, 500); // match animation duration
    }
  }, [show]);

  const getActionTitle = (type) => {
    switch (type) {
      case "shortlist":
        return "Shortlist this candidate?";
      case "reject":
      case "Rejected":
        return "Reject this candidate?";
      case "Hold":
        return "Hold this candidate?";
      case "Backout":
        return "Back-out this candidate?";
      case "Hire":
        return "Hire this candidate?";
      case "InterviewScheduled":
        return "Schedule interview for this candidate?";
      case "Select":
        return "Select this candidate?";
      case "BGV":
        return "Background Verification this candidate?";
      case "Assessment":
        return "Send Assessment to this candidate?";
      case "OfferRelease":
        return "Released Offer to this candidate?";
      default:
        return "Confirm action?";
    }
  };

  const getButtonText = (type) => {
    switch (type) {
      case "shortlist":
        return "Shortlist";
      case "reject":
      case "Rejected":
        return "Reject";
      case "Hold":
        return "Hold";
      case "Backout":
        return "Back-out";
      case "InterviewScheduled":
        return "Schedule";
      case "Select":
        return "Select";
      case "BGV":
        return "Background Verification";
      case "Assessment":
        return "Send Assessment";
      case "OfferRelease":
        return "Release Offer";
      case "Hire":
        return "Hire Candidate";
      default:
        return "Confirm";
    }
  };

  const getButtonColor = (type) => {
    switch (type) {
      case "reject":
      case "Rejected":
      case "Backout":
        return "bg-red-500";
      default:
        return "bg-primary";
    }
  };

  if (!shouldRender) return null;

  return (
    <>
      {shouldRender && (
        <div
          className="fixed inset-0 z-50 bg-black/40 overflow-y-auto"
          onClick={(e) => handleOutsideClick(e, onClose)}
        >
          <div
            onClick={onClose}
            className={`fixed inset-0 z-50 flex justify-end transition-opacity duration-500 ${isVisible ? "bg-black/40 opacity-100" : "bg-black/0 opacity-0"}`}
          >
            {" "}
            <div
              onClick={(e) => e.stopPropagation()}
              className={`bg-white h-full w-[50%] shadow-[ -10px_0px_30px_rgba(0,0,0,0.15) ] transform transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "translate-x-0" : "translate-x-full"}`}
            >
              {systemRemarkLoading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70">
                  <div className="h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              <div className="h-full flex flex-col">
                {/* HEADER */}
                <div className="flex justify-between items-center px-6 py-4 border-b">
                  <h2 className="text-xl font-semibold">Candidate Details</h2>
                  <button onClick={onClose}>
                    <X size={22} />
                  </button>
                </div>

                {candidateDetails ? (
                  <div className="p-6 flex-1 overflow-y-auto space-y-6">
                    {/* INTERVIEW DETAILS SECTION */}
                    {showInterviewDetailsSection && (
                      <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-blue-600 uppercase mb-4">
                          Interview Details
                        </h3>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-center border border-gray-300">
                            <thead className="bg-blue-100">
                              <tr>
                                <th className="p-2 border">Sequence</th>
                                <th className="p-6 border">Interviewer</th>
                                <th className="p-2 border">Code</th>
                                <th className="p-2 border">Status</th>
                                <th className="p-2 border">Conclusion</th>
                                <th className="p-2 border">Remarks</th>
                                <th className="p-2 border">Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {Array.isArray(interviewDetails) &&
                              interviewDetails.length > 0 ? (
                                [...interviewDetails]
                                  .sort(
                                    (a, b) =>
                                      Number(a.interviewSequence) -
                                      Number(b.interviewSequence),
                                  )
                                  .map((item, index) => (
                                    <tr key={index} className="border-t">
                                      <td className="p-3 border">
                                        {item.interviewSequence ||
                                          item.sequence ||
                                          "-NA-"}
                                      </td>

                                      <td className="p-3 border">
                                        {item.interviewerName || "-NA-"}
                                      </td>

                                      <td className="p-3 border">
                                        {item.interviewerCode || "-NA-"}
                                      </td>

                                      <td className="p-3 border">
                                        {item.isInterviewCompleted === "1"
                                          ? "Completed"
                                          : item.isInterviewCompleted === "2"
                                            ? "Scheduled"
                                            : "Pending"}
                                      </td>

                                      <td className="p-3 border">
                                        {item.interviewConclusion ||
                                          item.conclusion ||
                                          "-NA-"}
                                      </td>

                                      <td className="p-3 border">
                                        {item.interviewRemark ||
                                          item.remarks ||
                                          "-NA-"}
                                      </td>

                                      <td className="p-3 border">
                                        {item.isInterviewCompleted === "1" ? (
                                          <button
                                            onClick={() => onViewFeedback(item)}
                                            className="border border-primary text-primary px-3 py-1 rounded-md text-xs hover:bg-primary hover:text-white transition"
                                          >
                                            View Feedback
                                          </button>
                                        ) : item.isInterviewCompleted ===
                                          "0" ? (
                                          <button
                                            onClick={() =>
                                              onScheduleClick(item)
                                            }
                                            className="border border-primary text-primary px-3 py-1 rounded-md text-xs hover:bg-primary hover:text-white transition"
                                          >
                                            Schedule Interview
                                          </button>
                                        ) : item.isInterviewCompleted ===
                                          "2" ? (
                                          <button
                                            onClick={() =>
                                              onScheduleClick(item)
                                            }
                                            className="border border-primary text-primary px-3 py-1 rounded-md text-xs hover:bg-primary hover:text-white transition"
                                          >
                                            Reschedule Interview
                                          </button>
                                        ) : (
                                          <span className="text-gray-400 text-xs">
                                            -NA-
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                              ) : (
                                <tr>
                                  <td colSpan="7" className="p-4 text-gray-500">
                                    No Interview Details Found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* CANDIDATE CARD */}
                    <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-blue-600 uppercase">
                            {candidateDetails.candidateDetails?.name || "-NA-"}
                          </h3>

                          {selectedCandidate?.status && (
                            <span className="flex items-center gap-2 px-4 py-1 text-sm font-medium bg-primary text-white rounded-full shadow">
                              <Activity size={16} className="text-white" />
                              {selectedCandidate.status}
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2">
                          {showResumeButton && (
                            <button
                              onClick={onViewResume}
                              className="bg-primary text-white px-4 py-1 rounded-md text-sm"
                            >
                              View Resume
                            </button>
                          )}

                          {showSystemRemarkButton && (
                            <button
                              onClick={onViewSystemRemark}
                              className="bg-gradient-to-r from-blue-800 to-blue-400 text-white px-4 py-1 rounded-md text-sm"
                            >
                              System Remark
                            </button>
                          )}
                        </div>
                      </div>

                      <hr className="my-4" />

                      {/* DETAILS GRID */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        <Info
                          icon={<Mail size={16} className="text-primary" />}
                          label="Email"
                          value={candidateDetails.candidateDetails?.email}
                        />
                        <Info
                          icon={<Phone size={16} className="text-primary" />}
                          label="Mobile"
                          value={candidateDetails.candidateDetails?.mobile}
                        />
                        <Info
                          icon={<MapPin size={16} className="text-primary" />}
                          label="Location"
                          value={
                            candidateDetails.candidateDetails?.currentLocation
                          }
                        />
                        <Info
                          icon={<MapPin size={16} className="text-primary" />}
                          label="Preferred Location"
                          value={
                            candidateDetails.candidateDetails
                              ?.preferredLocation || "N/A"
                          }
                        />
                        <Info
                          icon={
                            <Briefcase size={16} className="text-primary" />
                          }
                          label="Total Experience (in years)"
                          value={
                            candidateDetails.candidateDetails
                              ?.totalExperience || 0
                          }
                        />
                        <Info
                          icon={
                            <IndianRupee size={16} className="text-primary" />
                          }
                          label="Current CTC"
                          value={
                            candidateDetails.candidateDetails?.currentCTC || 0
                          }
                        />
                        <Info
                          icon={
                            <TrendingUp size={16} className="text-primary" />
                          }
                          label="Expected CTC"
                          value={
                            candidateDetails.candidateDetails?.expectedCTC || 0
                          }
                        />
                        <Info
                          icon={<User size={16} className="text-primary" />}
                          label="Gender"
                          value={
                            candidateDetails.candidateDetails?.gender || "N/A"
                          }
                        />
                        <Info
                          icon={
                            <GraduationCap size={16} className="text-primary" />
                          }
                          label="Qualification"
                          value={
                            candidateDetails.candidateDetails?.qualification ||
                            "N/A"
                          }
                        />

                        <Info
                          icon={
                            <Building2 size={16} className="text-primary" />
                          }
                          label="Current Company"
                          value={
                            candidateDetails.candidateDetails?.currentCompany ||
                            "N/A"
                          }
                        />

                        <Info
                          icon={<FileText size={16} className="text-primary" />}
                          label="Remarks"
                          value={
                            candidateDetails.candidateDetails?.remarks || "N/A"
                          }
                        />
                        <Info
                          icon={
                            <CalendarCheck size={16} className="text-primary" />
                          }
                          label="Notice Period (in days)"
                          value={
                            candidateDetails.candidateDetails?.noticePeriod || 0
                          }
                        />
                        <Info
                          icon={<Plane size={16} className="text-primary" />}
                          label="Ready to Relocate"
                          value={
                            candidateDetails.candidateDetails?.readytoRelocate
                          }
                        />
                        <Info
                          icon={
                            <MessageCircle size={16} className="text-primary" />
                          }
                          label="Reason"
                          value={
                            candidateDetails.candidateDetails?.reasonofLeaving
                          }
                        />
                      </div>
                    </div>

                    {/* JOB DETAILS */}
                    <div className="bg-gray-50 rounded-xl shadow-sm">
                      <div className="flex justify-between items-center px-6 py-4">
                        <h3 className="text-lg font-semibold text-blue-600 uppercase">
                          {candidateDetails.jobDetails?.jobTitel || "-NA-"}
                        </h3>

                        <button
                          onClick={() => setIsJobOpen(!isJobOpen)}
                          className="border border-blue-500 p-1 rounded-md"
                        >
                          {isJobOpen ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </button>
                      </div>

                      {isJobOpen && (
                        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                          <Info
                            icon={
                              <Briefcase size={16} className="text-primary" />
                            }
                            label="Function"
                            value={candidateDetails.jobDetails?.functionName}
                          />

                          <Info
                            icon={
                              <Building2 size={16} className="text-primary" />
                            }
                            label="Department"
                            value={candidateDetails.jobDetails?.department}
                          />

                          <Info
                            icon={<Layers size={16} className="text-primary" />}
                            label="Grade"
                            value={candidateDetails.jobDetails?.grade}
                          />

                          <Info
                            icon={<MapPin size={16} className="text-primary" />}
                            label="Location"
                            value={candidateDetails.jobDetails?.location}
                          />

                          <Info
                            icon={<Map size={16} className="text-primary" />}
                            label="Zone"
                            value={candidateDetails.jobDetails?.zone}
                          />

                          <Info
                            icon={
                              <IndianRupee size={16} className="text-primary" />
                            }
                            label="Offered Salary"
                            value={`₹ ${candidateDetails.jobDetails?.offeredSalary}`}
                          />

                          <Info
                            icon={
                              <GraduationCap
                                size={16}
                                className="text-primary"
                              />
                            }
                            label="Qualification"
                            value={
                              candidateDetails.jobDetails?.minimumQualification
                            }
                          />

                          <Info
                            icon={
                              <TrendingUp size={16} className="text-primary" />
                            }
                            label="Min Experience (in years)"
                            value={
                              candidateDetails.jobDetails?.minimumExperience
                            }
                          />

                          <Info
                            icon={<User size={16} className="text-primary" />}
                            label="Gender"
                            value={
                              candidateDetails.jobDetails?.genderPreference
                            }
                          />
                        </div>
                      )}
                    </div>

                    {/* JOB INTERVIEW SECTION */}
                    {showInterviewSection && (
                      <div className="bg-white rounded-2xl shadow-md border">
                        {/* HEADER */}
                        <div className="bg-gradient-to-r from-blue-700 to-blue-500 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
                          <h4 className="text-lg font-semibold">
                            Interviewers
                          </h4>

                          <button
                            onClick={onOpenAddInterviewer}
                            className="bg-white text-blue-600 px-4 py-1.5 text-sm rounded-md font-medium hover:bg-gray-100 transition"
                          >
                            + Add Interviewer
                          </button>
                        </div>

                        <div className="p-5 overflow-x-auto">
                          <DragDropContext onDragEnd={handleDragEnd}>
                            <table className="w-full text-sm text-center border border-gray-200">
                              <thead className="bg-gray-200">
                                <tr>
                                  <th className="p-3 border">Code</th>
                                  <th className="p-3 border">Name</th>
                                  <th className="p-3 border">Email</th>
                                  <th className="p-3 border">Sequence</th>
                                  <th className="p-3 border">Action</th>
                                </tr>
                              </thead>

                              <Droppable droppableId="interviewers">
                                {(provided) => (
                                  <tbody
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                  >
                                    {interviewerList.map((item, index) => (
                                      <Draggable
                                        key={item.interviewerCode || index}
                                        draggableId={
                                          item.interviewerCode || `row-${index}`
                                        }
                                        index={index}
                                      >
                                        {(dragProvided) => (
                                          <tr
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            className="hover:bg-gray-50 cursor-grab"
                                          >
                                            <td className="p-3 border">
                                              {item.interviewerCode || "-"}
                                            </td>
                                            <td className="p-3 border">
                                              {item.interviewerName || "-"}
                                            </td>
                                            <td className="p-3 border">
                                              {item.interviewerEmail || "-"}
                                            </td>
                                            <td className="p-3 border">
                                              {item.interviewSequence}
                                            </td>
                                            <td className="p-3 border">
                                              <button
                                                onClick={() => {
                                                  const updated = [
                                                    ...interviewerList,
                                                  ];
                                                  updated.splice(index, 1);

                                                  const resequenced =
                                                    updated.map((it, i) => ({
                                                      ...it,
                                                      interviewSequence: String(
                                                        i + 1,
                                                      ),
                                                    }));

                                                  setInterviewerList(
                                                    resequenced,
                                                  );
                                                }}
                                                className="bg-red-500 text-white px-3 py-1 text-xs rounded-md hover:bg-red-600"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        )}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </tbody>
                                )}
                              </Droppable>
                            </table>
                          </DragDropContext>
                        </div>
                      </div>
                    )}

                    {modalActionType && (
                      <div className="bg-gray-50 p-4 rounded-lg border shadow-sm">
                        <h6 className="flex items-center gap-2 text-sm text-gray-700 font-medium mb-3">
                          ⚠ {getActionTitle(modalActionType)}
                        </h6>

                        {/*  Interview Fields */}
                        {modalActionType === "InterviewScheduled" && (
                          <>
                            <input
                              type="datetime-local"
                              value={interviewDateTime}
                              min={new Date().toISOString().slice(0, 16)}
                              onChange={(e) =>
                                setInterviewDateTime(e.target.value)
                              }
                              className="w-full border rounded-md p-2 text-sm mb-3"
                            />

                            <textarea
                              placeholder="Enter remark..."
                              value={remark}
                              onChange={(e) => setRemark(e.target.value)}
                              className="w-full border rounded-md p-2 text-sm"
                              rows={3}
                            />
                          </>
                        )}

                        {/*  Other Actions (Remark Only) */}
                        {modalActionType !== "InterviewScheduled" && (
                          <textarea
                            placeholder="Enter remark..."
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            className="w-full border rounded-md p-2 text-sm"
                            rows={3}
                          />
                        )}

                        <div className="flex justify-end gap-2 mt-3">
                          <button
                            onClick={onActionSubmit}
                            disabled={actionLoading}
                            className={`px-4 py-1.5 text-sm rounded-full text-white flex items-center gap-2 ${getButtonColor(
                              modalActionType,
                            )} ${actionLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                          >
                            {actionLoading ? (
                              <>
                                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                Processing...
                              </>
                            ) : (
                              getButtonText(modalActionType)
                            )}
                          </button>

                          <button
                            onClick={onClose}
                            className="px-4 py-1.5 text-sm rounded-full border"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    No candidate data selected.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Info = ({ icon, label, value }) => {
  return (
    <div className="flex items-start gap-3 min-w-0">
      {/* Icon */}
      <div className="mt-1">{icon}</div>

      {/* Content */}
      <div className="min-w-0">
        <p className="text-gray-400 text-xs">{label}</p>

        {/* Value */}
        <p
          className="text-gray-800 text-sm font-medium truncate max-w-[180px] md:max-w-[280px]"
          title={value}
        >
          {value || "N/A"}
        </p>
      </div>
    </div>
  );
};

export default CandidateDetailsModal;
